/**
 * QCoreSecure API client.
 * All requests go through REACT_APP_BACKEND_URL/api/v1/*
 * Access token stored in memory; refresh token in localStorage.
 */

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

let _accessToken = null;

export const tokenStore = {
  getAccess: () => _accessToken,
  setAccess: (t) => { _accessToken = t; },
  getRefresh: () => localStorage.getItem("qcs_refresh"),
  setRefresh: (t) => localStorage.setItem("qcs_refresh", t),
  clearRefresh: () => localStorage.removeItem("qcs_refresh"),
  clear: () => { _accessToken = null; localStorage.removeItem("qcs_refresh"); },
};

const STATUS_MESSAGES = {
  400: "Invalid request — please check your input",
  401: "Invalid email or password",
  403: "Access denied — you do not have permission to perform this action",
  404: "The requested resource could not be found",
  409: "An account with this email already exists",
  422: "Invalid request data — please check your input and try again",
  423: "Account locked — too many failed login attempts. Please wait before trying again",
  429: "Too many requests — please slow down and try again in a moment",
  500: "An unexpected server error occurred — please try again",
};

// Code-specific fallbacks used when the CDN strips/transforms HTTP status codes
// (e.g. some CDNs convert 423 → 401). If the JSON body has error.code but no
// error.message, these provide a meaningful fallback over the HTTP-status default.
const CODE_MESSAGES = {
  ACCOUNT_LOCKED: "Account locked due to too many failed login attempts. Please wait before trying again.",
  RATE_LIMITED: "Too many requests — please slow down and try again in a moment.",
  INVALID_CREDENTIALS: "Invalid email or password.",
  TOKEN_INVALID: "Your session has expired — please sign in again.",
  TOKEN_EXPIRED: "Your session has expired — please sign in again.",
  PERMISSION_DENIED: "You do not have permission to perform this action.",
  NOT_FOUND: "The requested resource could not be found.",
};

async function request(method, path, body, requiresAuth = false, retry = true) {
  const headers = { "Content-Type": "application/json" };
  if (requiresAuth && _accessToken) {
    headers["Authorization"] = `Bearer ${_accessToken}`;
  }

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Auto-refresh on 401
  if (res.status === 401 && requiresAuth && retry) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return request(method, path, body, requiresAuth, false);
    }
  }

  if (!res.ok) {
    let msg = STATUS_MESSAGES[res.status] || `Request failed (${res.status})`;
    let code = null;
    try {
      const err = await res.json();
      if (err?.error?.code) code = err.error.code;
      // Prefer the server's message; fall back to our code-specific message so
      // CDN status-code transforms (e.g. 423 → 401) never show a misleading fallback.
      if (err?.error?.message) msg = err.error.message;
      else if (code && CODE_MESSAGES[code]) msg = CODE_MESSAGES[code];
    } catch { /* body not readable — keep HTTP-status fallback */ }
    throw Object.assign(new Error(msg), { code, status: res.status });
  }

  // 204 No Content (e.g., DELETE) has no body — return null
  if (res.status === 204) return null;
  return res.json();
}

async function tryRefresh() {
  const rt = tokenStore.getRefresh();
  if (!rt) return false;
  try {
    const data = await request("POST", "/auth/refresh", { refresh_token: rt }, false, false);
    tokenStore.setAccess(data.access_token);
    tokenStore.setRefresh(data.refresh_token);
    return true;
  } catch {
    tokenStore.clear();
    return false;
  }
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export const authApi = {
  register: (body) => request("POST", "/auth/register", body),
  login: (body) => request("POST", "/auth/login", body),
  sendEmailOtp: (pre_auth_token) =>
    request("POST", "/auth/send-email-otp", { pre_auth_token }),
  verify2FA: (pre_auth_token, method, code) =>
    request("POST", "/auth/verify-2fa", { pre_auth_token, method, code }),
  verifyRecoveryCode: (pre_auth_token, recovery_code) =>
    request("POST", "/auth/verify-recovery-code", { pre_auth_token, recovery_code }),
  refresh: (refresh_token) => request("POST", "/auth/refresh", { refresh_token }),
  logout: (refresh_token) => request("POST", "/auth/logout", { refresh_token }, true),
  forgotPassword: (email) => request("POST", "/auth/forgot-password", { email }),
  resetPassword: (token, new_password) =>
    request("POST", "/auth/reset-password", { token, new_password }),
  changePassword: (current_password, new_password) =>
    request("POST", "/auth/change-password", { current_password, new_password }, true),

  // TOTP
  setupTOTP: () => request("POST", "/auth/setup-totp", null, true),
  confirmTOTP: (secret, code) => request("POST", "/auth/confirm-totp", { secret, code }, true),
  disableTOTP: () => request("DELETE", "/auth/totp", null, true),

  // Email OTP
  enableEmailOTP: () => request("POST", "/auth/email-otp/enable", null, true),
  disableEmailOTP: () => request("DELETE", "/auth/email-otp", null, true),

  // Recovery codes
  regenerateRecoveryCodes: () => request("POST", "/auth/recovery-codes/regenerate", null, true),
};

// ── Users ─────────────────────────────────────────────────────────────────────

export const usersApi = {
  me: () => request("GET", "/users/me", null, true),
  updateMe: (body) => request("PUT", "/users/me", body, true),
  list: (skip = 0, limit = 50) => request("GET", `/users/?skip=${skip}&limit=${limit}`, null, true),
  getUser: (id) => request("GET", `/users/${id}`, null, true),
  updateRole: (id, role) => request("PUT", `/users/${id}/role`, { role }, true),
};

// ── API Keys ──────────────────────────────────────────────────────────────────

export const apiKeysApi = {
  list: () => request("GET", "/api-keys/", null, true),
  create: (body) => request("POST", "/api-keys/", body, true),
  update: (id, body) => request("PUT", `/api-keys/${id}`, body, true),
  revoke: (id) => request("DELETE", `/api-keys/${id}`, null, true),
};

// ── Plans ─────────────────────────────────────────────────────────────────────

export const plansApi = {
  list: () => request("GET", "/plans/", null, true),
  listPublic: () => request("GET", "/plans/public", null, true),
  get: (id) => request("GET", `/plans/${id}`, null, true),
  create: (body) => request("POST", "/plans/", body, true),
  update: (id, body) => request("PUT", `/plans/${id}`, body, true),
  delete: (id) => request("DELETE", `/plans/${id}`, null, true),
};

// ── Tenants ───────────────────────────────────────────────────────────────────

export const tenantsApi = {
  list: (skip = 0, limit = 50) => request("GET", `/tenants/?skip=${skip}&limit=${limit}`, null, true),
  get: (id) => request("GET", `/tenants/${id}`, null, true),
  create: (body) => request("POST", "/tenants/", body, true),
  update: (id, body) => request("PUT", `/tenants/${id}`, body, true),
  stats: (id) => request("GET", `/tenants/${id}/stats`, null, true),
};

// ── QRNG ──────────────────────────────────────────────────────────────────────

export const qrngApi = {
  randomBytes: (count = 32) => request("GET", `/random/bytes?count=${count}`, null, true),
  randomInt: (min = 0, max = 100) => request("GET", `/random/integer?min=${min}&max=${max}`, null, true),
  randomFloat: () => request("GET", "/random/float", null, true),
  randomUUID: () => request("GET", "/random/uuid", null, true),
  randomSequence: (count, min, max) => request("POST", "/random/sequence", { count, min_val: min, max_val: max }, true),
  vrfGenerate: (input_data, key_id = "default") => request("POST", "/vrf/generate", { input_data, key_id }, true),
  vrfVerify: (output, proof, input_data, nonce) => request("POST", "/vrf/verify", { output, proof, input_data, nonce }, true),
  health: () => request("GET", "/random/health", null, true),
  stats: () => request("GET", "/stats", null, true),
};

// ── Archival ──────────────────────────────────────────────────────────────────

export const archivalApi = {
  getConfig: () => request("GET", "/archival/config", null, true),
  updateConfig: (body) => request("PUT", "/archival/config", body, true),
  getStats: () => request("GET", "/archival/stats", null, true),
  listJobs: () => request("GET", "/archival/jobs", null, true),
  createJob: (body) => request("POST", "/archival/jobs", body, true),
  listRequests: () => request("GET", "/archival/requests", null, true),
  createRequest: (body) => request("POST", "/archival/requests", body, true),
};

// ── Widgets ───────────────────────────────────────────────────────────────────

export const widgetsApi = {
  list: () => request("GET", "/widgets/", null, true),
  create: (body) => request("POST", "/widgets/", body, true),
  update: (id, body) => request("PUT", `/widgets/${id}`, body, true),
  remove: (id) => request("DELETE", `/widgets/${id}`, null, true),
  execute: (body) => request("POST", "/widgets/execute", body, true),
  seedDefaults: () => request("POST", "/widgets/seed-defaults", null, true),
};

// ── Reports ───────────────────────────────────────────────────────────────────

export const reportsApi = {
  list: () => request("GET", "/reports/", null, true),
  create: (body) => request("POST", "/reports/", body, true),
  update: (id, body) => request("PUT", `/reports/${id}`, body, true),
  remove: (id) => request("DELETE", `/reports/${id}`, null, true),
  run: (id) => request("POST", `/reports/${id}/run`, null, true),
  drilldown: (id, body) => request("POST", `/reports/${id}/drilldown`, body, true),
  seedDefaults: () => request("POST", "/reports/seed-defaults", null, true),
};

// ── Help & Support ────────────────────────────────────────────────────────────

export const helpApi = {
  listArticles: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request("GET", `/help/articles${qs ? `?${qs}` : ""}`, null, true);
  },
  getArticle: (id) => request("GET", `/help/articles/${id}`, null, true),
  // Admin
  adminListArticles: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request("GET", `/admin/help/articles${qs ? `?${qs}` : ""}`, null, true);
  },
  adminCreate: (body) => request("POST", "/admin/help/articles", body, true),
  adminUpdate: (id, body) => request("PUT", `/admin/help/articles/${id}`, body, true),
  adminDelete: (id) => request("DELETE", `/admin/help/articles/${id}`, null, true),
  adminTogglePublish: (id) => request("PATCH", `/admin/help/articles/${id}/toggle-publish`, null, true),
  adminSeed: () => request("POST", "/admin/help/articles/seed", null, true),
};
